<template>
  <Tooltip :disabled="!tooltip" side="top" :disable-closing-trigger="true">
    <span class="inline-flex shrink-0">
      <Input
        :model-value="draftValue"
        type="text"
        inputmode="numeric"
        :maxlength="2"
        :size="props.size"
        :disabled="disabled"
        :class="cn('w-12 h-8 bg-key text-center text-black-white tabular-nums', props.class)"
        @update:model-value="handleDraft"
        @blur="commitValue"
        @keydown.enter.prevent="onEnter"
      />
    </span>
    <template #content>
      <div
        class="text-s-medium text-white-black p-1 bg-floating-tooltip-container rounded-m whitespace-nowrap"
      >
        {{ tooltip }}
      </div>
    </template>
  </Tooltip>
</template>

<script setup lang="ts">
  import { HtmlHTMLAttributes, ref, watch } from 'vue'
  import { cn, Input, Tooltip } from '@rapoo/element'
  import { InputVariants } from '@rapoo/element/components/ui/input'

  const props = withDefaults(
    defineProps<{
      /** 正常显示的数值；两侧值不同而无法显示单一值时传入 `/`。 */
      modelValue: string
      /** 可输入的最小值与最大值。 */
      min: number
      max: number
      disabled?: boolean
      /** 仅在两侧数值不一致时传入，用于显示说明浮层。 */
      tooltip?: string
      size?: InputVariants['size']
      class?: HtmlHTMLAttributes['class']
    }>(),
    {
      size: 'sm',
    },
  )

  const emit = defineEmits<{
    commit: [value: number]
  }>()

  const draftValue = ref('')
  const isEditing = ref(false)

  watch(
    () => props.modelValue,
    (value) => {
      if (!isEditing.value) draftValue.value = value
    },
    { immediate: true },
  )

  watch(
    () => props.disabled,
    (disabled) => {
      if (!disabled) return
      isEditing.value = false
      draftValue.value = props.modelValue
    },
  )

  function handleDraft(value: string | number) {
    if (props.disabled) return
    isEditing.value = true
    draftValue.value = String(value).replace(/\D/g, '').slice(0, 2)
  }

  function commitValue() {
    isEditing.value = false
    const raw = Number(draftValue.value)
    if (!Number.isFinite(raw) || draftValue.value === '') {
      draftValue.value = props.modelValue
      return
    }

    const value = Math.min(props.max, Math.max(props.min, raw))
    draftValue.value = String(value)
    emit('commit', value)
  }

  function onEnter(event: KeyboardEvent) {
    ;(event.target as HTMLInputElement).blur()
  }
</script>
